import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { optimizeBackgroundImage } from '../components/common/OptimizedBackgroundImage';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  MapPinIcon,
  ClockIcon,
  StarIcon,
  PlayIcon,
  ArrowLeftIcon,
  GlobeAltIcon,
  CalendarIcon,
  SparklesIcon,
  HeartIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { placesApi, unescoApi, gamesApi, imagesApi, placeMetadataApi } from '../services/api';
import DesignTokens from '../styles/DesignTokens';
import GlassmorphismTokens from '../styles/GlassmorphismTokens';
import SEO from '../components/seo/SEO';
import ImageGallery from '../components/common/ImageGallery';
import { generatePlaceSchema, generateBreadcrumbSchema } from '../utils/structuredData';

const PageContainer = styled.div`
  padding-top: 80px;
  background: #000c1a;
  background-image: url(${GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  min-height: 100vh;
  padding-bottom: 100px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${GlassmorphismTokens.gradient.scenicOverlay};
    z-index: 0;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding-top: 0;
    padding-bottom: 80px;
  }

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding-bottom: 0;
  }
`;

// Compact header for places (when source=places) - glass style
const CompactHeader = styled.div`
  background: ${GlassmorphismTokens.glass.medium};
  backdrop-filter: ${GlassmorphismTokens.blur.lg};
  -webkit-backdrop-filter: ${GlassmorphismTokens.blur.lg};
  border-bottom: ${GlassmorphismTokens.border.light};
  padding: 1rem 0;
  position: sticky;
  top: 80px;
  z-index: 100;
  box-shadow: ${GlassmorphismTokens.shadow.glass};

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(40, 40, 60, 0.9);
  }
`;

const CompactHeaderContent = styled.div`
  max-width: ${DesignTokens.container.xl};
  margin: 0 auto;
  padding: 0 ${DesignTokens.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const CompactHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const CompactBackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #93C5FD;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CompactTitleSection = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompactTitle = styled.h1`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize.xl};
  }
`;

const CompactLocation = styled.div`
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 0.25rem;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CompactActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    justify-content: stretch;
  }
`;

const CompactActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: 600;
  font-size: ${DesignTokens.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  border: ${props => props.primary ? 'none' : GlassmorphismTokens.border.light};
  background: ${props => props.primary ? GlassmorphismTokens.gradient.blueCTA : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  box-shadow: ${props => props.primary ? GlassmorphismTokens.shadow.blueCTA : 'none'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.primary ? GlassmorphismTokens.shadow.blueCTAHover : GlassmorphismTokens.shadow.glass};
    background: ${props => props.primary ? GlassmorphismTokens.gradient.blueCTAHover : 'rgba(255, 255, 255, 0.15)'};
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 1;
    justify-content: center;
  }
`;

const HeroImageSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 500px;
  max-height: 800px;
  overflow: hidden;
  background: ${props => props.theme.colors.gradientPrimary};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 50vh;
    min-height: 400px;
    max-height: 600px;
  }
`;

const HeroImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => optimizeBackgroundImage(props.imageUrl || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828', { width: 1920, height: 1080, quality: 85 })});
  background-size: cover;
  background-position: center;
  opacity: 0.7;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.6) 100%);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  color: white;
`;

const Container = styled.div`
  max-width: ${DesignTokens.container.xl};
  margin: 0 auto;
  padding: 0 ${DesignTokens.spacing.md};
`;

const BackButton = styled(motion(Link))`
  position: absolute;
  top: 2rem;
  left: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  text-decoration: none;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: ${props => props.theme.borderRadius.full};
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(-4px);
  }
`;

const ActionButtons = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1rem;
  z-index: 10;
  width: 100%;
  max-width: 400px;
  padding: 0 1rem;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    bottom: 2rem;
    left: auto;
    right: 2rem;
    transform: none;
    max-width: none;
    width: auto;
  }
`;

const ActionButton = styled(motion.button)`
  flex: 1;
  height: 48px;
  background: ${props => props.primary ? 'white' : 'rgba(255, 255, 255, 0.2)'};
  backdrop-filter: blur(10px);
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'};
  border-radius: ${props => props.theme.borderRadius.full};
  color: ${props => props.primary ? props.theme.colors.primary : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.primary ? props.theme.colors.primaryLight : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 0;
    width: auto;
    padding: 0 1.5rem;
  }
`;

const PlaceTitle = styled(motion.h1)`
  font-size: ${DesignTokens.typography.fontSize['5xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  margin-bottom: ${DesignTokens.spacing.md};
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  letter-spacing: ${DesignTokens.typography.letterSpacing.tight};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${DesignTokens.typography.fontSize['4xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${DesignTokens.typography.fontSize['3xl']};
  }
`;

const PlaceLocation = styled(motion.div)`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  margin-bottom: ${DesignTokens.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.2);

  svg {
    width: 20px;
    height: 20px;
  }
`;

const QuickInfo = styled(motion.div)`
  display: flex;
  gap: ${DesignTokens.spacing.xl};
  flex-wrap: wrap;
  margin-top: ${DesignTokens.spacing.lg};
`;

const QuickInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 500;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ContentSection = styled.section`
  padding: ${props => props.compact ? `${DesignTokens.spacing.xl} 0` : `${DesignTokens.spacing['3xl']} 0`};
  background: white;
`;

const SectionTitle = styled.h2`
  font-size: ${DesignTokens.typography.fontSize['3xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${DesignTokens.spacing.xl};
  letter-spacing: ${DesignTokens.typography.letterSpacing.tight};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${DesignTokens.typography.fontSize['2xl']};
  }
`;

// Description component removed - no longer used (consolidated into Historical Significance card)

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${DesignTokens.spacing.xl};
  margin-bottom: ${DesignTokens.spacing['3xl']};
`;

const InfoCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  padding: ${DesignTokens.spacing.xl};
  border-radius: ${DesignTokens.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
  transition: all ${DesignTokens.transition.slow} ${DesignTokens.transition.easing};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradientPrimary};
    transform: scaleX(0);
    transition: transform ${DesignTokens.transition.slow} ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${DesignTokens.shadow.xl};
    border-color: ${props => props.theme.colors.primaryLight};

    &::before {
      transform: scaleX(1);
    }
  }
`;

const InfoCardIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${props => props.theme.colors.primaryLight};
  border-radius: ${DesignTokens.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${DesignTokens.spacing.lg};
  color: ${props => props.theme.colors.primary};

  svg {
    width: 28px;
    height: 28px;
  }
`;

const InfoCardTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize.xl};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${DesignTokens.spacing.md};
`;

const InfoCardContent = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  font-size: ${DesignTokens.typography.fontSize.base};
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.xs};
  margin-top: ${DesignTokens.spacing.sm};
`;

const Tag = styled(motion.span)`
  display: inline-block;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.medium};
  text-transform: capitalize;
`;

const CuisineList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${DesignTokens.spacing.sm} 0 0 0;
`;

const CuisineItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${DesignTokens.spacing.xs};
  padding: ${DesignTokens.spacing.xs} 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${DesignTokens.typography.fontSize.sm};
  
  &::before {
    content: '🍽️';
    font-size: ${DesignTokens.typography.fontSize.base};
  }
`;

const GamesSection = styled.section`
  padding: ${DesignTokens.spacing['3xl']} 0;
  background: ${props => props.theme.colors.surface};
`;

const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: ${DesignTokens.spacing.xl};
`;

const GameCard = styled(motion.div)`
  background: white;
  padding: ${DesignTokens.spacing.xl};
  border-radius: ${DesignTokens.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.borderLight};
  box-shadow: ${DesignTokens.shadow.md};
  transition: all ${DesignTokens.transition.slow} ${DesignTokens.transition.easing};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradientSecondary};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${DesignTokens.shadow['2xl']};
  }
`;

const GameIcon = styled.div`
  width: 64px;
  height: 64px;
  background: ${props => props.theme.colors.accentLight};
  border-radius: ${DesignTokens.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${DesignTokens.spacing.lg};
  color: ${props => props.theme.colors.accent};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const GameTitle = styled.h3`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${DesignTokens.spacing.sm};
`;

const GameType = styled.span`
  display: inline-block;
  padding: ${DesignTokens.spacing.xs} ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.primaryLight};
  color: ${props => props.theme.colors.primary};
  border-radius: ${DesignTokens.borderRadius.full};
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  margin-bottom: ${DesignTokens.spacing.md};
  text-transform: uppercase;
  letter-spacing: ${DesignTokens.typography.letterSpacing.wide};
`;

const GameDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: ${DesignTokens.typography.lineHeight.relaxed};
  margin-bottom: ${DesignTokens.spacing.lg};
  font-size: ${DesignTokens.typography.fontSize.base};
`;

const GameMeta = styled.div`
  display: flex;
  gap: ${DesignTokens.spacing.lg};
  margin-bottom: ${DesignTokens.spacing.lg};
  font-size: ${DesignTokens.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  flex-wrap: wrap;
`;

const PlayButton = styled(motion(Link))`
  display: inline-flex;
  align-items: center;
  gap: ${DesignTokens.spacing.sm};
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${DesignTokens.spacing.md} ${DesignTokens.spacing.xl};
  border-radius: ${DesignTokens.borderRadius.full};
  text-decoration: none;
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  font-size: ${DesignTokens.typography.fontSize.base};
  transition: all ${DesignTokens.transition.slow} ${DesignTokens.transition.easing};
  box-shadow: ${DesignTokens.shadow.lg};

  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    box-shadow: ${DesignTokens.shadow.xl};
    transform: translateY(-2px);
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

function PlaceDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Determine which API to use based on query parameter or try both
  // If source=unesco, use only UNESCO API
  // If source=places, use only Places API
  // Otherwise, try Places API first (since home page uses places), then UNESCO
  const apiSource = searchParams.get('source'); // 'places' or 'unesco'
  
  // Invalidate any stale place queries when ID changes to prevent cache issues
  useEffect(() => {
    if (id) {
      const placeId = String(id);
      // Clear any cached place data for this ID to ensure fresh fetch
      queryClient.removeQueries(['place', placeId]);
      queryClient.removeQueries(['placeMetadata']);
    }
  }, [id, queryClient]);

  // Use only ONE API based on source to avoid ID collision issues
  // CRITICAL: Don't mix APIs - IDs can overlap between places and unesco tables
  const { data: placeResponse, isLoading: placeLoading, error: placeError } = useQuery(
    ['place', String(id), apiSource], // Include source in query key
    async () => {
      // Capture id as string to ensure consistency
      const placeId = String(id);
      
      // Helper to convert unesco site to place format (must be defined before use)
      const convertUnescoToPlace = (unescoSite) => {
        if (!unescoSite) return null;
        
        let images = [];
        let imageUrl = unescoSite.imageUrl || unescoSite.image_url || null;
        
        if (unescoSite.additionalMetadata) {
          try {
            const metadata = typeof unescoSite.additionalMetadata === 'string' 
              ? JSON.parse(unescoSite.additionalMetadata) 
              : unescoSite.additionalMetadata;
            
            if (metadata.images && Array.isArray(metadata.images)) {
              images = metadata.images;
              if (!imageUrl && images.length > 0) {
                imageUrl = images[0];
              }
            }
          } catch (e) {
          }
        }
        
        return {
          id: unescoSite.id,
          name: unescoSite.name,
          country: unescoSite.country,
          city: unescoSite.region,
          description: unescoSite.description,
          imageUrl: imageUrl,
          images: images,
          category: unescoSite.category,
          region: unescoSite.region,
          yearOfInscription: unescoSite.yearOfInscription,
          latitude: unescoSite.latitude,
          longitude: unescoSite.longitude,
          wikidataId: unescoSite.wikidataId,
          wikipediaUrl: unescoSite.wikipediaUrl,
          criteria: unescoSite.criteria,
          endangered: unescoSite.endangered,
        };
      };
      
      // If source is specified, use only that API
      if (apiSource === 'unesco') {
        try {
          const response = await unescoApi.getById(placeId);
          // Axios wraps the response in response.data
          const unescoSite = response?.data;
          
          if (!unescoSite) {
            throw new Error(`UNESCO site not found with ID: ${placeId}`);
          }
          // Verify ID matches
          if (String(unescoSite.id) !== placeId) {
            throw new Error(`ID mismatch: requested ${placeId}, got ${unescoSite.id}`);
          }
          const converted = convertUnescoToPlace(unescoSite);
          return converted;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Error fetching UNESCO site:', {
              message: error?.message, 
              response: error?.response?.data,
              status: error?.response?.status
            });
          }
          throw error;
        }
      }
      
      if (apiSource === 'places') {
        try {
          const response = await placesApi.getById(placeId);
          const place = response?.data;
          
          if (process.env.NODE_ENV === 'development') {
            console.debug('📍 Fetched place:', {
              hasData: !!place, 
              id: place?.id, 
              name: place?.name 
            });
          }
          
          if (!place) {
            throw new Error(`Place not found with ID: ${placeId}`);
          }
          // Verify ID matches
          if (String(place.id) !== placeId) {
            throw new Error(`ID mismatch: requested ${placeId}, got ${place.id}`);
          }
          return place;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Error fetching place:', error);
          }
          throw error;
        }
      }
      
      // Auto-detect: Try Places API first (default for home page), then UNESCO
      const [placesResult, unescoResult] = await Promise.allSettled([
        placesApi.getById(placeId).catch(e => ({ error: e })),
        unescoApi.getById(placeId).catch(e => ({ error: e }))
      ]);
      
      // Auto-detect mode: Try Places API first (for home page), then UNESCO (for heritage page)
      // CRITICAL: Only use ONE API's result - don't mix them
      
      // Try Places API first (default for home page)
      if (placesResult.status === 'fulfilled' && !placesResult.value.error) {
        const place = placesResult.value?.data;
        if (place && String(place.id) === placeId) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('✅ Place ID verified:', {
              requestedId: placeId,
              returnedId: place?.id,
              name: place?.name,
              country: place?.country
            });
          }
          return place;
        }
      }
      
      // Fall back to UNESCO API only if Places API didn't return valid data
      if (unescoResult.status === 'fulfilled' && !unescoResult.value.error) {
        const unescoSite = unescoResult.value?.data;
        if (unescoSite && String(unescoSite.id) === placeId) {
          const converted = convertUnescoToPlace(unescoSite);
          if (process.env.NODE_ENV === 'development') {
            console.debug('✅ UNESCO site ID verified:', {
              requestedId: placeId,
              returnedId: converted?.id,
              name: converted?.name,
              country: converted?.country
            });
          }
          return converted;
        }
      }
      
      // If both failed, throw an error
      const placesError = placesResult.status === 'rejected' ? placesResult.reason : (placesResult.value?.error);
      const unescoError = unescoResult.status === 'rejected' ? unescoResult.reason : (unescoResult.value?.error);
      
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Both APIs failed:', {
          places: placesError?.response?.status || placesError?.message,
          unesco: unescoError?.response?.status || unescoError?.message
        });
      }
      
      throw placesError || unescoError || new Error('Place not found');
    },
    {
      enabled: !!id,
      retry: 1,
      // Prevent stale cache from causing wrong place to load
      staleTime: 0, // Always refetch when navigating to a new place
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    }
  );

  // Fetch games for this place
  const { data: gamesResponse } = useQuery(
    ['placeGames', id],
    () => gamesApi.getByPlaceId(id),
    {
      select: (response) => response?.data || [],
      enabled: !!id,
      retry: 1,
    }
  );

  // Fetch images from ImageLicense table if place doesn't have images
  const { data: placeImagesResponse } = useQuery(
    ['placeImages', id],
    () => imagesApi.getPlaceImages(id),
    {
      select: (response) => {
        const images = response?.data || [];
        return images.map(img => img.imageUrl).filter(Boolean);
      },
      enabled: !!id && (!placeResponse?.images || placeResponse.images.length === 0),
      retry: 1,
      staleTime: 5 * 60 * 1000,
    }
  );

  // Create a stable place object to prevent mutation issues
  // CRITICAL: Only use place if ID matches URL ID to prevent wrong place from being used
  const place = useMemo(() => {
    if (!placeResponse) return null;
    
    // Verify the place ID matches the URL ID
    const placeId = String(placeResponse.id);
    const urlId = String(id);
    
    if (placeId !== urlId) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ CRITICAL: Place ID mismatch in useMemo! REJECTING!', {
          urlId: urlId,
          placeId: placeId,
          placeName: placeResponse.name,
          placeCountry: placeResponse.country
        });
      }
      // Return null to prevent using wrong place
      return null;
    }
    
    // Additional safety check for Petra (ID 1269)
    if (urlId === '1269' && placeResponse.name?.toLowerCase() !== 'petra') {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ CRITICAL: Wrong place name for Petra in useMemo! REJECTING!', {
          urlId: urlId,
          placeId: placeId,
          expectedName: 'Petra',
          actualName: placeResponse.name,
          actualCountry: placeResponse.country
        });
      }
      return null;
    }
    
    // Create a new object to avoid mutation
    const freshPlace = { ...placeResponse };
    
    
    return freshPlace;
  }, [placeResponse, id]);
  
  // Track if place is verified (ID matches and name is correct)
  const isPlaceVerified = useMemo(() => {
    if (!place) return false;
    const placeId = String(place.id);
    const urlId = String(id);
    
    // Check ID match
    if (placeId !== urlId) return false;
    
    // Additional check for Petra
    if (urlId === '1269' && place.name?.toLowerCase() !== 'petra') {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Place verification failed: Wrong name for Petra');
      }
      return false;
    }
    
    return true;
  }, [place, id]);

  // Debug logging to track place data
  useEffect(() => {
    if (place) {
      // Development-only debug logging
      if (process.env.NODE_ENV === 'development') {
        if (String(place.id) !== String(id)) {
          console.error('❌ CRITICAL: ID MISMATCH!', {
            urlId: id,
            placeId: place.id,
            placeName: place.name,
            placeCountry: place.country
          });
        }
        
        if (String(id) === '1269' && place.name?.toLowerCase() !== 'petra') {
          console.error('❌ CRITICAL: Wrong place loaded for Petra!', {
            expectedName: 'Petra',
            actualName: place.name,
            actualCountry: place.country,
            placeId: place.id
          });
        }
      }
    }
  }, [place, id]);

  // Fetch enhanced place metadata (tags, cuisine, etc.) - must be after place is defined
  // CRITICAL: Use placeResponse directly instead of place object to avoid cache/mutation issues
  const { data: placeMetadata } = useQuery(
    ['placeMetadata', String(id)], // Only use URL ID in key - don't include name to avoid cache collisions
    async () => {
      const urlId = String(id);
      
      // Fetch place data directly from API to ensure we have the correct name
      // Don't rely on cached place object which might be wrong
      
      let placeName = null;
      let placeId = null;
      
      // Try to get place name from the current placeResponse (if available and verified)
      if (placeResponse && String(placeResponse.id) === urlId) {
        placeName = placeResponse.name;
        placeId = placeResponse.id;
      } else {
        // If placeResponse is not available or wrong, fetch directly from API
        // CRITICAL: Respect the source parameter - only call the appropriate API
        try {
          if (apiSource === 'unesco') {
            // Only call UNESCO API
            const response = await unescoApi.getById(urlId);
            const unescoSite = response?.data;
            if (unescoSite && String(unescoSite.id) === urlId) {
              placeName = unescoSite.name;
              placeId = unescoSite.id;
            }
          } else if (apiSource === 'places') {
            // Only call Places API
            const response = await placesApi.getById(urlId);
            const place = response?.data;
            if (place && String(place.id) === urlId) {
              placeName = place.name;
              placeId = place.id;
            }
          } else {
            // Auto-detect: Try both APIs (only when source is not specified)
            const [placesResult, unescoResult] = await Promise.allSettled([
              placesApi.getById(urlId).catch(e => ({ error: e })),
              unescoApi.getById(urlId).catch(e => ({ error: e }))
            ]);
            
            // Try Places API first
            if (placesResult.status === 'fulfilled' && !placesResult.value.error) {
              const place = placesResult.value?.data;
              if (place && String(place.id) === urlId) {
                placeName = place.name;
                placeId = place.id;
              }
            }
            
            // Fall back to UNESCO API if Places didn't work
            if (!placeName && unescoResult.status === 'fulfilled' && !unescoResult.value.error) {
              const unescoSite = unescoResult.value?.data;
              if (unescoSite && String(unescoSite.id) === urlId) {
                placeName = unescoSite.name;
                placeId = unescoSite.id;
              }
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ Error fetching place name:', error);
          }
        }
      }
      
      // CRITICAL: Verify we have the correct place name
      if (!placeName) {
        return Promise.reject(new Error('No place name available'));
      }
      
      if (String(placeId) !== urlId) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ CRITICAL: Place ID mismatch!', {
            urlId: urlId,
            placeId: placeId,
            placeName: placeName
          });
        }
        return Promise.reject(new Error(`Place ID mismatch: expected ${urlId}, got ${placeId}`));
      }
      
      // Final safety check - if name contains "Petroglyphs" but we're looking for Petra, reject
      if (urlId === '1269' && placeName.toLowerCase().includes('petroglyphs')) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ CRITICAL: Wrong place name detected! Expected Petra, got:', placeName);
        }
        return Promise.reject(new Error(`Wrong place name: expected Petra, got ${placeName}`));
      }
      
      return placeMetadataApi.getEnhancedMetadata(placeName);
    },
    {
      select: (response) => response?.data,
      enabled: !!id && (isPlaceVerified || !!placeResponse), // Only fetch if we have verified place or placeResponse
      retry: false, // Don't retry on error to prevent wrong data
      staleTime: 0, // Always refetch to prevent stale data
    }
  );
  const games = gamesResponse || [];
  
  // Merge images from ImageLicense if available
  const allImages = useMemo(() => {
    if (place?.images && place.images.length > 0) {
      return place.images;
    }
    if (placeImagesResponse && placeImagesResponse.length > 0) {
      return placeImagesResponse;
    }
    if (place?.imageUrl) {
      return [place.imageUrl];
    }
    return [];
  }, [place?.images, place?.imageUrl, placeImagesResponse]);

  if (placeLoading) {
    return (
      <PageContainer>
        <LoadingState>
          <div className="loading-spinner"></div>
          <span style={{ marginLeft: '1rem' }}>Loading destination details...</span>
        </LoadingState>
      </PageContainer>
    );
  }

  if (placeError || !place) {
    const errorBackPath = apiSource === 'unesco' ? '/heritage' : '/places';
    return (
      <PageContainer>
        <Container>
          <BackButton to={errorBackPath}>
            <ArrowLeftIcon className="w-5 h-5" />
            Back
          </BackButton>
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Destination not found or unable to load.</p>
            <Link to={errorBackPath} style={{ color: '#4285F4' }}>
              Return to all destinations
            </Link>
          </div>
        </Container>
      </PageContainer>
    );
  }

  const imageUrl = place.imageUrl || `https://images.unsplash.com/photo-${1500000000000 + (place.id || 0)}?w=1920&q=80`;

  // Generate SEO data
  const placeName = place.name || 'Heritage Site or Famous Place';
  const location = place.city && place.country 
    ? `${place.city}, ${place.country}`
    : place.country || '';
  
  // SEO title (used in SEO component)
  const seoTitle = location 
    ? `${placeName}, ${location} | SafarGlimpse.com`
    : `${placeName} | SafarGlimpse.com`;
  
  const seoDescription = place.description || place.historicalSignificance || 
    `Explore ${placeName}${location ? ` in ${location}` : ''}. Discover its rich history, cultural significance, and plan your visit with interactive content and detailed information.`;

  // Generate structured data
  const placeSchema = generatePlaceSchema(place);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/', },
    { name: 'Destinations', url: '/places' },
    { name: placeName, url: `/places/${place.id}` },
  ]);

  const structuredData = [placeSchema, breadcrumbSchema].filter(Boolean);

  // Check if we should use compact layout (for places and unesco sources)
  const isCompactLayout = apiSource === 'places' || apiSource === 'unesco';
  
  // Determine back button destination based on source
  const backButtonPath = apiSource === 'unesco' ? '/heritage' : '/places';

  return (
    <PageContainer>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={imageUrl}
        type="article"
        structuredData={structuredData}
      />
      
      {isCompactLayout ? (
        // Compact header for places and heritage
        <CompactHeader>
          <CompactHeaderContent>
            <CompactHeaderLeft>
              <CompactBackButton to={backButtonPath}>
                <ArrowLeftIcon />
                Back
              </CompactBackButton>
              <CompactTitleSection>
                <CompactTitle>{place.name}</CompactTitle>
                <CompactLocation>
                  <MapPinIcon />
                  {place.city ? `${place.city}, ` : ''}{place.country}
                </CompactLocation>
              </CompactTitleSection>
            </CompactHeaderLeft>
            <CompactActionButtons>
              <CompactActionButton
                onClick={() => setIsFavorite(!isFavorite)}
              >
                {isFavorite ? <HeartSolidIcon /> : <HeartIcon />}
                Save
              </CompactActionButton>
              <CompactActionButton
                primary
                onClick={() => window.location.href = `/itineraries?place=${place.id}`}
              >
                <ShareIcon />
                Share
              </CompactActionButton>
            </CompactActionButtons>
          </CompactHeaderContent>
        </CompactHeader>
      ) : (
        // Full hero section (fallback for auto-detect mode)
        <HeroImageSection>
          <HeroImage imageUrl={imageUrl} />
          <BackButton 
            to={backButtonPath}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back
          </BackButton>
          <ActionButtons>
            <ActionButton
              onClick={() => setIsFavorite(!isFavorite)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {isFavorite ? <HeartSolidIcon /> : <HeartIcon />}
              Save
            </ActionButton>
            <ActionButton
              primary
              onClick={() => window.location.href = `/recommendations?place=${place.id}`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <ShareIcon />
              Share & Itinerary
            </ActionButton>
          </ActionButtons>
          <HeroContent>
            <div>
              <PlaceTitle
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {place.name}
              </PlaceTitle>
              <PlaceLocation
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <MapPinIcon />
                {place.city ? `${place.city}, ` : ''}{place.country}
              </PlaceLocation>
            <QuickInfo
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <QuickInfoItem>
                <StarSolidIcon style={{ fill: '#FBBC04', color: '#FBBC04' }} />
                <span>4.8</span>
              </QuickInfoItem>
              {place.difficultyLevel && (
                <QuickInfoItem>
                  <SparklesIcon />
                  <span>Level {place.difficultyLevel}</span>
                </QuickInfoItem>
              )}
              {/* Games hidden for launch - coming soon */}
              {false && place.gamesCount > 0 && (
                <QuickInfoItem>
                  <PlayIcon />
                  <span>{place.gamesCount} Game{place.gamesCount > 1 ? 's' : ''}</span>
                </QuickInfoItem>
              )}
            </QuickInfo>
          </div>
        </HeroContent>
        </HeroImageSection>
      )}

      <ContentSection compact={isCompactLayout}>
        <Container>
          {/* Image Gallery */}
          {allImages.length > 0 && (
            <ImageGallery
              images={allImages}
              mainImage={allImages[0]}
            />
          )}

          <SectionTitle>About This Destination</SectionTitle>
          {(placeMetadata?.historicalSignificance || placeMetadata?.whyMustVisit || placeMetadata?.interestingFacts || 
            place.historicalSignificance || place.interestingFacts || place.bestTimeToVisit || place.description) ? (
            <InfoGrid>
              {/* Historical Significance - prefer enhanced metadata - ONLY SHOWN ONCE */}
              {(placeMetadata?.historicalSignificance || place.historicalSignificance || place.description) && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <GlobeAltIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Historical Significance</InfoCardTitle>
                  <InfoCardContent>
                    {typeof placeMetadata?.historicalSignificance === 'object' && placeMetadata.historicalSignificance?.text
                      ? placeMetadata.historicalSignificance.text
                      : placeMetadata?.historicalSignificance || place.historicalSignificance || place.description}
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Why Must Visit - from enhanced metadata */}
              {placeMetadata?.whyMustVisit && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <SparklesIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Why Must Visit</InfoCardTitle>
                  <InfoCardContent>
                    {Array.isArray(placeMetadata.whyMustVisit) ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {placeMetadata.whyMustVisit.map((item, index) => (
                          <li key={index} style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 0 }}>•</span>
                            {typeof item === 'object' && item?.text ? item.text : item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      typeof placeMetadata.whyMustVisit === 'object' && placeMetadata.whyMustVisit?.text
                        ? placeMetadata.whyMustVisit.text
                        : placeMetadata.whyMustVisit
                    )}
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Interesting Facts - prefer enhanced metadata */}
              {(placeMetadata?.interestingFacts || place.interestingFacts) && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <SparklesIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Fascinating Facts</InfoCardTitle>
                  <InfoCardContent>
                    {Array.isArray(placeMetadata?.interestingFacts) ? (
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {placeMetadata.interestingFacts.map((item, index) => (
                          <li key={index} style={{ marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 0 }}>•</span>
                            {typeof item === 'object' && item?.text ? item.text : item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      typeof placeMetadata?.interestingFacts === 'object' && placeMetadata.interestingFacts?.text
                        ? placeMetadata.interestingFacts.text
                        : placeMetadata?.interestingFacts || place.interestingFacts
                    )}
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Best Time to Visit - prefer enhanced metadata */}
              {(placeMetadata?.bestTimeToVisit || place.bestTimeToVisit) && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <CalendarIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Best Time to Visit</InfoCardTitle>
                  <InfoCardContent>
                    {typeof placeMetadata?.bestTimeToVisit === 'string' 
                      ? placeMetadata.bestTimeToVisit 
                      : placeMetadata?.bestTimeToVisit?.text || place.bestTimeToVisit}
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Vibe Tags */}
              {placeMetadata?.tagVibe && placeMetadata.tagVibe.length > 0 && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <SparklesIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Vibe & Atmosphere</InfoCardTitle>
                  <InfoCardContent>
                    <TagsContainer>
                      {placeMetadata.tagVibe.map((tag, index) => (
                        <Tag
                          key={tag}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.4 + index * 0.05 }}
                          whileHover={{ scale: 1.1 }}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </TagsContainer>
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Local Cuisine */}
              {placeMetadata?.localCuisine && placeMetadata.localCuisine.length > 0 && (
                <InfoCard
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ y: -4 }}
                >
                  <InfoCardIcon>
                    <SparklesIcon />
                  </InfoCardIcon>
                  <InfoCardTitle>Local Cuisine</InfoCardTitle>
                  <InfoCardContent>
                    <CuisineList>
                      {placeMetadata.localCuisine.map((item, index) => (
                        <CuisineItem key={index}>
                          {typeof item === 'string' ? item : item.dish}
                        </CuisineItem>
                      ))}
                    </CuisineList>
                  </InfoCardContent>
                </InfoCard>
              )}

              {/* Description removed - now consolidated into Historical Significance above */}
            </InfoGrid>
          ) : (
            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -4 }}
            >
              <InfoCardIcon>
                <GlobeAltIcon />
              </InfoCardIcon>
              <InfoCardTitle>About {place.name}</InfoCardTitle>
              <InfoCardContent>
                {place.country && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Location:</strong> {place.city ? `${place.city}, ` : ''}{place.country}
                  </p>
                )}
                {place.category && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Category:</strong> {place.category}
                  </p>
                )}
                {place.yearOfInscription && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Year of Inscription:</strong> {place.yearOfInscription}
                  </p>
                )}
                {place.wikipediaUrl && (
                  <p style={{ marginTop: '1rem' }}>
                    <a 
                      href={place.wikipediaUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#4285F4', textDecoration: 'underline' }}
                    >
                      Learn more on Wikipedia →
                    </a>
                  </p>
                )}
              </InfoCardContent>
            </InfoCard>
          )}

        </Container>
      </ContentSection>

      {/* Games section hidden for launch - coming soon */}
      {false && games && games.length > 0 && (
        <GamesSection>
          <Container>
            <SectionTitle>Interactive Games</SectionTitle>
            <GamesGrid>
              {games.map((game, index) => (
                <GameCard
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <GameIcon>
                    <PlayIcon />
                  </GameIcon>
                  <GameTitle>{game.name}</GameTitle>
                  <GameType>{game.gameType}</GameType>
                  <GameDescription>{game.description || game.instructions}</GameDescription>
                  <GameMeta>
                    {game.timeLimit && (
                      <span>
                        <ClockIcon className="w-4 h-4 inline mr-1" />
                        {Math.floor(game.timeLimit / 60)} min
                      </span>
                    )}
                    {game.maxScore && (
                      <span>
                        <StarIcon className="w-4 h-4 inline mr-1" />
                        Max: {game.maxScore} pts
                      </span>
                    )}
                  </GameMeta>
                  <PlayButton 
                    to={game.id === 'stonehenge-main' ? '/game/stonehenge' : `/game/${game.id}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <PlayIcon className="w-5 h-5" />
                    Play Game
                  </PlayButton>
                </GameCard>
              ))}
            </GamesGrid>
          </Container>
        </GamesSection>
      )}
    </PageContainer>
  );
}

export default PlaceDetailPage;
